import React from "react";
import { motion } from "framer-motion";
import { Plane, Radio, Clock, Check, AlertTriangle, CalendarRange } from "lucide-react";
import { eur } from "../lib/api";
import { useI18n, verdictKey } from "../i18n";
import { formatTripDate } from "../lib/goal";

// Presentational LIVE flight section. Renders ONLY when `data.mode === "live"` with
// recommended offers; otherwise nothing. Fetching + lifecycle are owned by Results.
export const LiveOffers = ({ data }) => {
  const { t, lang } = useI18n();

  if (!data || data.mode !== "live" || !data.recommended?.length) return null;

  const time = (iso) => { try { return new Date(iso).toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
  const fmt = (v) => `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} ${data.recommended[0].currency}`;

  return (
    <div className="mb-8" data-testid="live-offers">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="dv-pill text-xs px-3 py-1.5 font-display" style={{ background: "var(--book)", color: "#fff" }} data-testid="live-badge">
          <span className="relative flex h-2 w-2 mr-1"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>
          {t("results.liveBadge")}
        </span>
        <h2 className="font-display font-bold text-xl">{t("results.liveTitle")}</h2>
        <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--paper)", border: "1px solid var(--line)", color: "var(--muted)" }}>{t("results.liveFlightOnly")}</span>
        <span className="text-xs text-slate-500 flex items-center gap-1.5"><Radio size={12} color="var(--book)" /> {t("results.livePriceChecked", { t: time(data.fetched_at) })}</span>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {data.recommended.map((o, i) => {
          const book = o.decision === "BOOK";
          const c = book ? { m: "var(--book)", ink: "var(--book-ink)", soft: "var(--book-soft)" } : { m: "var(--wait)", ink: "var(--wait-ink)", soft: "var(--wait-soft)" };
          return (
            <motion.div key={o.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="dv-card p-5 flex flex-col" data-testid={`live-offer-${i}`} style={{ borderColor: i === 0 ? "var(--book-soft)" : "var(--line)" }}>
              <div className="flex items-center justify-between">
                <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--ink)", color: "#fff" }}>{o.origin} → {o.destination}</span>
                {o.badge === "BEST MATCH" && <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}>{t("results.liveBestMatch")}</span>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><Plane size={16} color="var(--brand)" /></div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm truncate">{o.destination_name || o.destination}</div>
                  <div className="text-[11px] text-slate-400 truncate">{o.marketing_carriers?.join(" · ") || "—"}</div>
                </div>
              </div>
              {(o.departure_date || o.return_date) && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700" data-testid={`live-dates-${i}`}>
                  <CalendarRange size={13} color="var(--brand)" />
                  {formatTripDate(o.departure_date, lang)} → {formatTripDate(o.return_date, lang)}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                <span className="dv-pill px-2 py-0.5" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>{o.stops === 0 ? t("results.liveDirect") : t("results.liveStops", { n: o.stops })}</span>
                <span className="dv-pill px-2 py-0.5" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}><Clock size={10} /> {o.travel_hours}h</span>
                {o.baggage_included && <span className="dv-pill px-2 py-0.5" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>{t("results.liveBags")}</span>}
              </div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="dv-total text-2xl text-slate-900">{fmt(o.flight_total)}</div>
                  <div className="text-[11px] text-slate-500 max-w-[16rem] leading-snug" data-testid={`live-flight-note-${i}`}>
                    {o.budget_left >= 0
                      ? t("results.liveFlightRemain", { x: eur(o.budget_left) })
                      : t("results.liveFlightOver", { x: eur(-o.budget_left) })}
                  </div>
                </div>
                <span className="dv-pill text-[11px] px-2.5 py-1 font-display" style={{ background: c.soft, color: c.ink }}>
                  {book ? <Check size={12} /> : <AlertTriangle size={11} />} {t(verdictKey(o.decision))}
                </span>
              </div>
              {o.decision_reasons?.[0] && <div className="mt-3 text-xs text-slate-600 flex items-start gap-1.5"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: c.m }} /> {o.decision_reasons[0]}</div>}
              <div className="mt-3 pt-3 border-t text-[10px] text-slate-400 flex items-center gap-1" style={{ borderColor: "var(--line)" }}>
                <Radio size={10} /> {t("results.liveExpires", { t: time(o.expires_at) })}
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-3 text-[11px] text-slate-400">{t("results.liveNote")}</div>
    </div>
  );
};
